/**
 * Direct-fetch client for the KDS read endpoints under
 * `/api/v1/merchant/kds/*`. These aren't in the generated OpenAPI `paths`
 * map yet, so — like `features/orders/hooks.ts#fetchAdminOrdersList` and
 * `features/merchants/hooks.ts#fetchAdminMerchantsList` — we hit them with a
 * hand-rolled fetch carrying the stored access token.
 *
 * Two things this helper does that the existing fallbacks don't, because the
 * KDS needs them:
 *   1. On 401 it runs the shared refresh mutex once and retries (8h+ kitchen
 *      shifts outlive the access token — EC-013); on refresh failure it
 *      clears tokens and bounces to login, mirroring `api/client.ts`.
 *   2. On error it parses the `ErrorResponse` envelope so callers can read
 *      `error.code` (e.g. `MERCHANT_SELECTION_REQUIRED`, `NO_MERCHANT_LINKED`)
 *      and `error.details.eligibleMerchants` for the store selector (RF-004).
 *
 * Status transitions (`PATCH /api/v1/orders/{id}/status`) go through the
 * typed `api` client instead — that path IS generated. See
 * `hooks/useOrderTransition.ts`.
 */
import { ApiError, type ApiErrorPayload } from '@favelafood/shared/api/errors';
import { clearTokens, getAccessToken } from '@favelafood/shared/auth/tokens';
import { tryRefresh } from '@favelafood/shared/auth/refresh';
import { forceLogin } from '@favelafood/shared/auth/keycloak';
import type {
  KdsCard,
  KdsDeliveryAddress,
  KdsDeliveryState,
  KdsHistoryPage,
  KdsOrderDetail,
  KdsRecentOrder,
  KdsSettings,
  KdsSlaOverrides,
  KdsTodaySummary,
  MerchantMembership,
  OrderStatus,
} from './types';

const baseUrl = import.meta.env.VITE_API_BASE ?? '';
const KDS_BASE = '/api/v1/merchant/kds';

/** Parse a money/`BigDecimal` field (arrives as a JSON string — bug-pattern #7). */
const parseDecimal = (raw: unknown): number => {
  if (raw === null || raw === undefined) return 0;
  const n = typeof raw === 'string' ? Number(raw) : Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const parseNullableDouble = (raw: unknown): number | null => {
  if (raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const appendMerchant = (path: string, merchantId?: string): string =>
  merchantId ? `${path}?merchantId=${encodeURIComponent(merchantId)}` : path;

const parseErrorPayload = async (response: Response): Promise<ApiErrorPayload> => {
  try {
    const body = (await response.clone().json()) as unknown;
    if (typeof body === 'object' && body !== null) return body as ApiErrorPayload;
  } catch {
    /* fall through */
  }
  return { message: response.statusText };
};

const sendWithAuth = (path: string, init?: RequestInit): Promise<Response> => {
  const token = getAccessToken();
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(`${baseUrl}${path}`, { ...init, headers });
};

/** Authorized JSON fetch with one-shot 401 refresh + structured errors. */
const kdsFetch = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  let response = await sendWithAuth(path, init);

  if (response.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await sendWithAuth(path, init);
    } else {
      clearTokens();
      forceLogin(); // OIDC → kc.login(); sem instância KC → raiz (PanelGuard assume).
      throw new ApiError(401, { message: 'Session expired' }, 'Session expired');
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, await parseErrorPayload(response), `HTTP ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
};

// --- Wire shapes (raw, pre-mapping). Money fields arrive as strings. ---

interface RawKdsItem {
  productName?: string;
  quantity?: number;
  options?: string[];
}
interface RawKdsCard {
  orderId: string;
  displayCode?: string | null;
  status: OrderStatus;
  customerFirstName?: string | null;
  paymentMethod?: string | null;
  totalAmount?: string | number | null;
  deliveryType?: string | null;
  notes?: string | null;
  createdAt: string;
  /** Deadline do aceite (`OrderAcceptanceSla`); ver `mapCard`. */
  acceptanceDeadlineAt?: string | null;
  acceptedAt?: string | null;
  preparationStartedAt?: string | null;
  readyAt?: string | null;
  courierName?: string | null;
  deliveryState?: KdsDeliveryState | null;
  courierEtaToStoreMinutes?: number | null;
  /** BigDecimal → chega como STRING (bug-pattern #7), igual a `totalAmount`. */
  courierDistanceToStoreKm?: string | number | null;
  courierEtaToCustomerMinutes?: number | null;
  itemCount?: number;
  items?: RawKdsItem[];
}
interface RawRecentOrder {
  orderId: string;
  /** Colunas CÓDIGO e ITENS do Histórico do dia (RF-128); ver `mapRecent`. */
  displayCode?: string | null;
  status: OrderStatus;
  customerFirstName?: string | null;
  itemCount?: number;
  totalAmount?: string | number | null;
  finishedAt?: string | null;
}
interface RawKdsDeliveryAddress {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  referencePoint?: string | null;
}
interface RawKdsOrderDetail {
  orderId: string;
  displayCode?: string | null;
  status: OrderStatus;
  customerFirstName?: string | null;
  paymentMethod?: string | null;
  deliveryType?: string | null;
  notes?: string | null;
  items?: RawKdsItem[];
  deliveryAddress?: RawKdsDeliveryAddress | null;
  courierName?: string | null;
  courierPhone?: string | null;
  deliveryOfferedAt?: string | null;
  /** Os 5 abaixo são `BigDecimal` → chegam como STRING (bug-pattern #7). */
  subtotal?: string | number | null;
  deliveryFee?: string | number | null;
  serviceFee?: string | number | null;
  discountAmount?: string | number | null;
  couponCode?: string | null;
  totalAmount?: string | number | null;
}
interface RawPageResponse<T> {
  content?: T[];
  page?: number;
  totalElements?: number;
  last?: boolean;
}
interface RawKdsSettings {
  slaOverrides?: KdsSlaOverrides | null;
  kdsEnabled?: boolean | null;
  autoAcceptOrders?: boolean | null;
}
interface RawTodaySummary {
  completedCount?: number;
  completedRevenue?: string | number | null;
  cancelledCount?: number;
  avgPrepMinutes?: number | null;
  last20?: RawRecentOrder[];
}

/**
 * Wire → domínio. **Todo campo do `KdsOrderCard` precisa aparecer aqui**: um
 * campo que o backend envia e este mapper não copia morre em silêncio — foi
 * assim que o countdown de 4 min do aceite nunca acendeu em card nenhum,
 * enquanto o aviso do topo prometia a regra. Campo novo entra com teste em
 * `__tests__/api.test.ts` provando que chega ao card.
 */
const mapCard = (raw: RawKdsCard): KdsCard => ({
  orderId: raw.orderId,
  displayCode: raw.displayCode ?? null,
  status: raw.status,
  customerFirstName: raw.customerFirstName ?? null,
  paymentMethod: raw.paymentMethod ?? null,
  totalAmount: parseDecimal(raw.totalAmount),
  deliveryType: raw.deliveryType ?? null,
  notes: raw.notes ?? null,
  createdAt: raw.createdAt,
  acceptanceDeadlineAt: raw.acceptanceDeadlineAt ?? null,
  acceptedAt: raw.acceptedAt ?? null,
  preparationStartedAt: raw.preparationStartedAt ?? null,
  readyAt: raw.readyAt ?? null,
  courierName: raw.courierName ?? null,
  deliveryState: raw.deliveryState ?? null,
  // ETA/distância NUNCA caem em 0: `parseNullableDouble` preserva o nulo do
  // fail-open (EC-020) — `parseDecimal` viraria "0 min"/"0,0 km", que mente.
  courierEtaToStoreMinutes: parseNullableDouble(raw.courierEtaToStoreMinutes),
  courierDistanceToStoreKm: parseNullableDouble(raw.courierDistanceToStoreKm),
  courierEtaToCustomerMinutes: parseNullableDouble(raw.courierEtaToCustomerMinutes),
  itemCount: raw.itemCount ?? raw.items?.length ?? 0,
  items: mapItems(raw.items),
});

const mapItems = (raw: RawKdsItem[] | undefined): KdsCard['items'] =>
  (raw ?? []).map((i) => ({
    productName: i.productName ?? '',
    quantity: i.quantity ?? 1,
    // Os modificadores SÃO a observação por item no painel (RF-124): "bem
    // passado" é exatamente um deles. Descartá-los aqui apagaria a única
    // observação por item que existe no modelo.
    options: i.options ?? [],
  }));

/**
 * Wire → domínio do detalhamento (RF-124). **Mesma regra do `mapCard`**: campo
 * do `KdsOrderDetail` que não aparece aqui morre em silêncio — foi assim seis
 * vezes neste módulo. Campo novo entra com teste em `__tests__/api.test.ts`.
 *
 * Dois cuidados que este mapper tem e o `mapCard` não precisa:
 *   - os cinco valores são `BigDecimal` (string no JSON) e passam por
 *     `parseDecimal`: dinheiro ausente vale 0, que é o que a soma da tela pede;
 *   - `deliveryAddress` e `courierPhone` continuam **nulos** quando o backend os
 *     omite (LGPD, RF-124/RF-127). Nada de objeto vazio "pra facilitar": um
 *     endereço com campos em branco na tela é pior que a seção não existir.
 */
const mapDetail = (raw: RawKdsOrderDetail): KdsOrderDetail => ({
  orderId: raw.orderId,
  displayCode: raw.displayCode ?? null,
  status: raw.status,
  customerFirstName: raw.customerFirstName ?? null,
  paymentMethod: raw.paymentMethod ?? null,
  deliveryType: raw.deliveryType ?? null,
  notes: raw.notes ?? null,
  items: mapItems(raw.items),
  deliveryAddress: raw.deliveryAddress ? mapAddress(raw.deliveryAddress) : null,
  courierName: raw.courierName ?? null,
  courierPhone: raw.courierPhone ?? null,
  deliveryOfferedAt: raw.deliveryOfferedAt ?? null,
  subtotal: parseDecimal(raw.subtotal),
  deliveryFee: parseDecimal(raw.deliveryFee),
  serviceFee: parseDecimal(raw.serviceFee),
  discountAmount: parseDecimal(raw.discountAmount),
  couponCode: raw.couponCode ?? null,
  totalAmount: parseDecimal(raw.totalAmount),
});

const mapAddress = (raw: RawKdsDeliveryAddress): KdsDeliveryAddress => ({
  street: raw.street ?? null,
  number: raw.number ?? null,
  complement: raw.complement ?? null,
  neighborhood: raw.neighborhood ?? null,
  referencePoint: raw.referencePoint ?? null,
});

/** Mesma regra do `mapCard`: campo do `KdsRecentOrder` que não aparece aqui morre. */
const mapRecent = (raw: RawRecentOrder): KdsRecentOrder => ({
  orderId: raw.orderId,
  displayCode: raw.displayCode ?? null, // nulo = pedido pré-backfill (EC-027)
  status: raw.status,
  customerFirstName: raw.customerFirstName ?? null,
  itemCount: raw.itemCount ?? 0,
  totalAmount: parseDecimal(raw.totalAmount),
  finishedAt: raw.finishedAt ?? null,
});

/**
 * Idem para as configurações. Aqui a armadilha é outra: sem mapper o `as
 * KdsSettings` faria QUALQUER JSON passar por tipado, e campo que o backend
 * envia (o `autoAcceptOrders`, que ele preenche da coluna a cada leitura)
 * chegaria sem ninguém declarar. Ausente vira `null`, nunca `false`.
 */
const mapSettings = (raw: RawKdsSettings): KdsSettings => ({
  slaOverrides: raw.slaOverrides ?? null,
  kdsEnabled: raw.kdsEnabled ?? null,
  autoAcceptOrders: raw.autoAcceptOrders ?? null,
});

// --- Public API ---

/**
 * The caller's store memberships. Drives front-side merchant resolution
 * (RF-004): 0 → no store linked / admin must pass ?merchantId; 1 → that
 * store; >1 → show the selector.
 */
export const getMyMerchants = (): Promise<MerchantMembership[]> =>
  kdsFetch<MerchantMembership[]>('/api/v1/merchants/me');

/** All active orders for the store (snapshot). RF-020. */
export const getKdsSnapshot = async (merchantId?: string): Promise<KdsCard[]> => {
  const raw = await kdsFetch<RawKdsCard[]>(appendMerchant(`${KDS_BASE}/orders`, merchantId));
  return (raw ?? []).map(mapCard);
};

/**
 * Detalhamento de UM pedido para o painel lateral (RF-124).
 *
 * **Sob demanda, nunca no snapshot** (RNF-K02): a tela fica aberta 12 h e o
 * snapshot é refeito a cada mudança e a cada 30 s no fallback — buscar o detalhe
 * de todo card multiplicaria requisição (e exposição de endereço) por card.
 * Quem chama é o painel ao abrir, um pedido por vez.
 */
export const getKdsOrderDetail = async (
  orderId: string,
  merchantId?: string,
): Promise<KdsOrderDetail> =>
  mapDetail(
    await kdsFetch<RawKdsOrderDetail>(
      appendMerchant(`${KDS_BASE}/orders/${encodeURIComponent(orderId)}`, merchantId),
    ),
  );

/** End-of-day operational summary (RF-070/071/072). */
export const getKdsTodaySummary = async (merchantId?: string): Promise<KdsTodaySummary> => {
  const raw = await kdsFetch<RawTodaySummary>(appendMerchant(`${KDS_BASE}/today-summary`, merchantId));
  return {
    completedCount: raw.completedCount ?? 0,
    completedRevenue: parseDecimal(raw.completedRevenue),
    cancelledCount: raw.cancelledCount ?? 0,
    avgPrepMinutes: parseNullableDouble(raw.avgPrepMinutes),
    last20: (raw.last20 ?? []).map(mapRecent),
  };
};

/** Quantos pedidos por página do histórico. O backend limita o `size`. */
export const KDS_HISTORY_PAGE_SIZE = 50;

/**
 * Histórico do DIA OPERACIONAL inteiro, paginado (RF-128).
 *
 * Substitui o `last20` do resumo, que é amostra: o backend corta em 20 e ainda
 * mistura entregues e cancelados, então a loja de 40 entregas nunca via metade
 * do próprio dia. A janela do dia (fuso da loja) é decidida no BACKEND
 * (`OperationalDay.now()`) a cada requisição — por isso a virada da meia-noite
 * (EC-024) se resolve rebuscando, e não mandando data nenhuma daqui.
 *
 * `URLSearchParams` no lugar do `appendMerchant` dos outros GETs porque aqui há
 * três parâmetros: concatenar `&page=` no que aquele helper devolve produziria
 * `/history&page=0` quando a loja vem do token (sem `?merchantId`).
 */
export const getKdsHistory = async (
  merchantId: string | undefined,
  page: number,
  size: number = KDS_HISTORY_PAGE_SIZE,
): Promise<KdsHistoryPage> => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (merchantId) params.set('merchantId', merchantId);
  const raw = await kdsFetch<RawPageResponse<RawRecentOrder>>(
    `${KDS_BASE}/history?${params.toString()}`,
  );
  return {
    content: (raw.content ?? []).map(mapRecent),
    page: raw.page ?? page,
    totalElements: raw.totalElements ?? 0,
    // Sem `last` no envelope a rolagem PARA. O contrário (assumir que há mais)
    // faria o "Carregar mais" pedir uma página vazia atrás da outra pra sempre.
    last: raw.last ?? true,
  };
};

/** Per-store KDS settings (server-persisted SLA overrides + flags). RF-040. */
export const getKdsSettings = async (merchantId?: string): Promise<KdsSettings> =>
  mapSettings(await kdsFetch<RawKdsSettings>(appendMerchant(`${KDS_BASE}/settings`, merchantId)));

export interface UpdateKdsSettingsBody {
  slaOverrides?: Partial<KdsSlaOverrides>;
  kdsEnabled?: boolean;
  /** Ausente = não mexer no aceite automático (é o que o KDS v1 manda hoje). */
  autoAcceptOrders?: boolean;
}

/** Persists per-store KDS settings. May 403 (admin) or 501 (column absent). */
export const putKdsSettings = async (
  body: UpdateKdsSettingsBody,
  merchantId?: string,
): Promise<KdsSettings> =>
  mapSettings(
    await kdsFetch<RawKdsSettings>(appendMerchant(`${KDS_BASE}/settings`, merchantId), {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  );
